/* ── BRIEF 126 §9 — THE EMAIL IS A DOCUMENT, NOT A BUTTON ───────────────────
 *
 * [RULED] Laurent: "the email link should just be added as a document,
 * triggering an icon and as such appear when the drawer is opened."
 *
 * 125 §4 built a bespoke control (`components/OpenEmailButton.tsx`) beside the
 * documents pane, for the case where Outlook gave no `webLink` and the doc row
 * was therefore omitted. §9 deletes that special case rather than decorating
 * it: THE ROW IS ADDED ALWAYS, and the link is resolved on demand when it is
 * not already known. Same cure as one modal, one participant list, one
 * notification row.
 *
 * ── WHAT GOES IN `url` WHEN THERE IS NO LINK, AND WHY IT IS NOT AN INVENTION ─
 *
 * `TaskDoc.url` and `AgendaEventDoc.url` are `String` — NOT NULL — so a row
 * with no link still needs one. It gets `message:<internetMessageId>`, which is
 *   · the RFC-2392-shaped URI Apple Mail and Outlook put on the pasteboard, and
 *   · the EXACT string `EmailDropLayer` already parses out of `text/uri-list`
 *     when the drop arrives (`/^message:/i`, decodeURIComponent, strip).
 * So the app is storing back the identifier it was handed, not minting a scheme.
 *
 * IT IS THE MESSAGE-ID, NOT THE LINK, WHICH IS 125 §4's RULING KEPT: "a
 * `webLink` rots, a Message-ID does not" (Phase 32, `billingDedupKey`). When
 * Outlook DID give a link the row still carries that link, exactly as it has
 * since brief 05 — there is nothing to resolve and no reason to defer it.
 *
 * ── IT NEVER WIDENS THE GUARD ──────────────────────────────────────────────
 *
 * `openDocHref` (SharedUI) is UNTOUCHED and still opens `http(s)` and bare
 * `www.` only. Widening it to take a `message:` URI is a separate,
 * security-relevant ruling and this brief does not make it. Callers ask
 * `isEmailDocUrl()` FIRST and route those rows here; everything else reaches
 * `openDocHref` exactly as before.
 *
 * ── AND NOTHING THAT WAS SAID STOPS BEING SAID ─────────────────────────────
 *
 * Every refusal 125 §4 built moves here verbatim — the named reason from the
 * server (`not_connected`, `not_found`, `no_link`), and the 502 whose own
 * sentence rides through `api()`'s error envelope because "I could not ask
 * Graph" and "it is not there" must never reach a caller as one answer (124's
 * ruling). What changes is where they are said: on the document row's open
 * path, which is now the only affordance.
 *
 * ── AND 130 §2 IS WHAT FINALLY APPLIES THAT RULING TO THE OTHER BRANCH ─────
 *
 * The three reasons named two paragraphs up were NAMED and then said with ONE
 * sentence. `refusalText` below is where they stop being. The 502 branch is
 * untouched — it was already right, and it is the model.
 */
import { api } from "../api";
import { openDocHref } from "../components/SharedUI";
import type { TFn } from "./taxonomyTypes";

/** The scheme `EmailDropLayer` parses on the way in and this writes on the way
 *  out. Lower-case here; readers match case-insensitively, as the layer does. */
const EMAIL_URI_PREFIX = "message:";

/** `message:<encoded Message-ID>` — what a doc row carries when no link is known. */
export function emailDocUrl(internetMessageId: string): string {
  return EMAIL_URI_PREFIX + encodeURIComponent(internetMessageId);
}

/** Is this document row the email one, i.e. must it be resolved before opening? */
export function isEmailDocUrl(url: unknown): boolean {
  return /^message:/i.test(String(url || "").trim());
}

/** The Message-ID back out of the row, or null if this is not one of ours. */
export function emailIdFromDocUrl(url: unknown): string | null {
  const raw = String(url || "").trim();
  if (!isEmailDocUrl(raw)) return null;
  const id = decodeURIComponent(raw.replace(/^message:/i, "")).trim();
  return id || null;
}

/* Resolved links are cached for the SESSION rather than for a mount, which is
   the one thing that changes from 125's control: the same row is now reachable
   from the drawer, the modal and the agenda row, and asking Graph three times
   for one email because three components mounted is not a cost worth paying.
   Nothing is persisted — a `webLink` rots, and the ruling that it is never
   stored is exactly why this is a Map and not a column. */
const resolved = new Map<string, string>();

/* ── BRIEF 130 §2 — ONE SENTENCE PER REASON ────────────────────────────────
 *
 * [RULED] Laurent, on the glance of #434: "NOT OK: says Outlook (allows drop)
 * does NOT provide email link, while does provide email ID." His screenshot:
 * *"⚠ Cet e-mail ne peut pas être ouvert d'ici — Outlook n'en donne aucun lien
 * (not_connected)"*.
 *
 * [MEASURED] `server/src/routes/microsoft.js:226-229` answers THREE distinct
 * reasons and this function collapsed all three into one sentence about
 * Outlook, with the cause appended as a raw code the reader was expected to
 * decode. `not_connected` means *this box has no Microsoft 365 connection* —
 * it says nothing whatever about Outlook. THE OLD SENTENCE WAS CORRECT FOR
 * EXACTLY ONE OF THE THREE, `no_link`, and that is the only place its wording
 * survives.
 *
 * IT IS 126's DEFECT, NOT 129's. This file's own header at :37-42 names all
 * three reasons — "the named reason from the server (`not_connected`,
 * `not_found`, `no_link`)" — and the one string was written anyway. A file that
 * knows the distinction and does not make it is the whole failure.
 *
 * ── WHY THE FAMILY LIVES HERE AND NOT IN `m365Status.ts` ──────────────────
 *
 * §2 says to decide between the two and say why. MEASURED, because the brief's
 * premise for the alternative does not hold: `m365Status.ts:12` is a COMMENT
 * inside a prose paragraph about what 126 §7 deliberately does NOT touch, not a
 * declaration of anything. That module has no sentence family, no `TFn`, no
 * toast and no `reason` in it at all; its four exports answer ONE question —
 * "is there an M365 account behind this control" — as a boolean plus a `null`,
 * and its consumers use the answer to decide what to RENDER.
 *
 * The family below is keyed on the `reason` field of ONE route,
 * `GET /auth/microsoft/message-link`, and this file is that route's only
 * caller. Two of its three values (`not_found`, `no_link`) are facts about a
 * MESSAGE and not about a connection, so hosting them in the connection module
 * would put two thirds of a family somewhere it does not belong to buy a
 * taxonomy. The key's owner is the family's home; no third home is created.
 *
 * ── THE FOURTH ROW IS THE DELIBERATE EXCEPTION ────────────────────────────
 *
 * The fence is "one sentence per reason, never one sentence with a code bolted
 * on" — and §2's own table asks the UNRECOGNISED arm to "keep the raw code
 * visible". That is not the defect wearing a smaller hat: a code nobody in this
 * tree has a sentence for is precisely the case where the raw value is the only
 * information there is, and the reader who reports it is the reason the next
 * row gets written. Known reasons never see a parenthesis.
 *
 * KEYS ARE LITERAL AT EACH ARM rather than a `Record` indexed by the reason:
 * `i18n-keys-complete` reads literal `t()` keys and cannot resolve a computed
 * one, and a family whose completeness no gate can check is a family that goes
 * missing in French.
 */
function refusalText(t: TFn, reason: string | null): string {
  const r = String(reason || "").trim();
  if (r === "not_connected")
    return t("emailLink.not_connected", {
      fallback:
        "Microsoft 365 is not connected, so no e-mail can be opened from here. Connect it in Settings → Microsoft 365.",
    });
  if (r === "not_found")
    return t("emailLink.not_found", {
      fallback:
        "This e-mail is not in the connected mailbox. Check that you are signed in to the mailbox that received it.",
    });
  if (r === "no_link")
    return t("emailLink.no_link", {
      fallback: "Outlook gives no link for this e-mail. Open it in Outlook to read it.",
    });
  const generic = t("emailLink.unknown", {
    fallback: "This e-mail cannot be opened from here.",
  });
  return r ? `${generic} (${r})` : generic;
}

function say(t: TFn, reason: string | null) {
  window.lxToast?.(refusalText(t, reason), "warning");
}

/**
 * Open the email a document row names. Returns true when a tab was opened.
 *
 * Verbatim from `OpenEmailButton`'s `onClick`, which retires with this commit:
 * the known-link fast path, the resolve, the named refusal, and the 502 that
 * carries the server's own sentence.
 */
export async function openEmailDocHref(url: unknown, t: TFn): Promise<boolean> {
  const id = emailIdFromDocUrl(url);
  if (!id) {
    say(t, null);
    return false;
  }
  const hit = resolved.get(id);
  if (hit) return openDocHref(hit);
  try {
    const r = (await api.get(
      `/auth/microsoft/message-link?internetMessageId=${encodeURIComponent(id)}`,
    )) as { webLink?: string; reason?: string | null } | null;
    const link = r && r.webLink;
    if (link) {
      resolved.set(id, link);
      return openDocHref(link);
    }
    say(t, (r && r.reason) || null);
    return false;
  } catch (e) {
    /* NOT swallowed (`no-silent-catch`): a 502 from the route means "I could
       not ask Graph", which the 124 ruling insists must not reach a human as
       "it is not there". The server's own sentence rides through `api()`'s
       error envelope. */
    window.lxToast?.(
      "⚠ " +
        ((e as Error)?.message ||
          t("emailLink.unavailable", {
            fallback: "This email cannot be opened from here",
          })),
      "warning",
    );
    return false;
  }
}
